import type { Question, QuotaGroup } from '@api/surveys/utilities/types';
import { Button, CopyToClipboard, Error, Loader, ModalV2 as Modal, Select } from '@utilities';
import type { ErrorProps } from '@utilities/components/Error';
import { getSelectNumericValue } from '@utilities/components/Select/utilities/helpers';
import { GlobeIcon, RepromptIcon } from '@utilities/icons';
import type { ModalProps } from '@utilities/components/ModalV2';

import {
  getDropdownOptions,
  type PreviewSelection,
  useCanPreviewSurveys,
  useIframeUrl,
  usePublicLink,
} from '../../utilities';

import styles from './_index.module.scss';

export interface PreviewModalProps extends Omit<ModalProps, 'children'> {
  instantSurveyId: number;
  onQuestionChange: (questionId: number) => void;
  onQuotaGroupChange: (quotaGroupId: number) => void;
  onRestart: () => void;
  previewSelection: PreviewSelection;
  questions: Question[];
  quotaGroups: QuotaGroup[];
}

type ErrorStatus = ErrorProps['status'];

const PreviewModal = ({
  instantSurveyId,
  onQuestionChange,
  onQuotaGroupChange,
  onRestart,
  previewSelection = {},
  questions,
  quotaGroups,
  ...props
}: PreviewModalProps) => {
  const { questionId, quotaGroupId } = previewSelection;
  const { error, iframeUrl, isLoading } = useIframeUrl({
    instantSurveyId,
    previewSelection,
  });
  const { isLoading: isLoadingPublicLink, publicLink } = usePublicLink({
    instantSurveyId,
    quotaGroupId,
  });

  const [canPreview] = useCanPreviewSurveys();
  if (!canPreview) return null;

  const showQuotaGroupDropdown = quotaGroups.length > 1;
  const quotaGroupOptions = getDropdownOptions(quotaGroups);
  const questionOptions = getDropdownOptions(questions);

  return (
    <Modal
      className={styles['modal-preview']}
      hasCloseIcon
      size="fullscreen"
      {...props}
      shouldSlide
    >
      <div className={styles['modal-preview-header']}>
        <div className={styles['modal-preview-header-toolbar']}>
          <div className={styles['modal-preview-header-dropdowns']}>
            {showQuotaGroupDropdown && (
              <Select
                className={styles['modal-preview-quota-group-dropdown']}
                onChange={(option) => {
                  const value = getSelectNumericValue(option);
                  if (value != null) onQuotaGroupChange(value);
                }}
                options={quotaGroupOptions}
                value={quotaGroupOptions.find(
                  (option) => String(option.value) === String(quotaGroupId)
                )}
              />
            )}
            {showQuotaGroupDropdown && (
              <span className={styles['modal-preview-vertical-divider']} />
            )}
            <Select
              className={styles['modal-preview-question-dropdown']}
              onChange={(option) => {
                const value = getSelectNumericValue(option);
                if (value != null) onQuestionChange(value);
              }}
              options={questionOptions}
              value={questionOptions.find((option) => option.value === questionId)}
            />
          </div>
          <Button
            className={styles['modal-preview-restart-button']}
            icon={<RepromptIcon />}
            onClick={onRestart}
            text="Restart"
          />
        </div>
        <CopyToClipboard
          className={styles['modal-preview-public-link-button']}
          finalTooltipText="Copied!"
          initialContent={<GlobeIcon />}
          isDisabled={isLoadingPublicLink}
          text="Public Link"
          value={publicLink}
        />
      </div>
      <div className={styles['modal-preview-content']}>
        <div className={styles['modal-preview-viewport']}>
          <div className={styles['modal-preview-mobile-device-frame']}>
            <Iframe error={error} isLoading={isLoading} src={iframeUrl} />
          </div>
        </div>
      </div>
    </Modal>
  );
};

interface IframeProps {
  error?: ErrorStatus;
  isLoading: boolean;
  src: string | null;
}

const Iframe = ({ error, isLoading, src }: IframeProps) => {
  if (isLoading || !src) {
    return (
      <div className={styles['modal-preview-loader']}>
        <Loader isCentered isVerticallyCentered />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles['modal-preview-error']}>
        <Error status={error} />
      </div>
    );
  }

  return (
    <iframe
      allow="camera; microphone; clipboard-write"
      className={styles['modal-preview-iframe']}
      loading="lazy"
      referrerPolicy="strict-origin-when-cross-origin"
      sandbox="allow-popups allow-scripts allow-same-origin"
      src={src}
      title="Instant Survey Preview"
    />
  );
};

export default PreviewModal;
